import React, { useLayoutEffect, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Container, Form } from "reactstrap";
import { Col, FormGroup, Row } from "reactstrap";
import { nanoid } from 'nanoid'
import { useFormik } from "formik";
import { generateInputTag, getInputSizing, Yup } from "./utils";
import LoadingScreen from "components/LoadingScreen";
import { ADDRESS_SELECT_INPUT_TYPE, DATATYPES, HIDDEN_INPUT_TYPE } from "utils/Constants";
import InputLabel from "./components/InputLabel";
import { trackFormData } from "utils/Analytics";

const DynamicForm = forwardRef((props, ref) => {
    const [formData, setFormData] = useState({});
    const [formStructure, setFormStructure] = useState(false);
    const [reRenderOptionFields, setReRenderOptionFields] = useState([]);
    const [visibleIdFields, setVisibleIdFields] = useState([]);
    const {
        className, inline, tag, children, formConfig, onSubmit, useExternalState = false,
        onInputChange, onStateChange, externalFormState = false, isLoading = false } = props;
    const hasExternalFormState = externalFormState && externalFormState.id;
    const isEditMode = externalFormState && externalFormState.id;
    const [prevFormData, setPrevFormData] = useState({});

    useLayoutEffect(() => {
        if (!!externalFormState) {
            setFormData(externalFormState);
        } else {
            setFormData({});
        }

    }, [hasExternalFormState, externalFormState]);

    useEffect(() => {
        const run = async () => {
            await loadDynamicValues(formConfig, formData, prevFormData);
            setPrevFormData(formData);
        };

        run();
    }, [formConfig, formData]);

    useEffect(async () => {
        // await loadDynamicValues(formConfig);
        await registerDynamicListeners(formConfig);
        await registerVisibleIdFields(formConfig);
    }, [formConfig]);


    const registerDynamicListeners = (formConfig) => {
        const reRenderFields = formConfig.sections.flatMap(s => s.fields).flat().filter(f => f.reFetchAfter)
        setReRenderOptionFields(reRenderFields)
    }

    const registerVisibleIdFields = (formConfig) => {
        const fields = formConfig.sections.flatMap(s => s.fields).flat().filter(f => f.visibleIf)
        setVisibleIdFields(fields)
    }

    const loadDynamicValues = async (formConfig, FormData, prevFormData) => {

        for (let section of formConfig.sections) {
            for (let group of section.fields) {
                for (let input of group) {
                    if (input.optionsFunction && input.optionsFunction instanceof Function) {
                        if (FormData[input.name] != prevFormData[input.name] || !input.options) {
                            const options = await input.optionsFunction(FormData);
                            input.options = options;
                        }
                    }
                }
            }
        }

        setFormStructure(formConfig);
    }

    const submitForm = (e) => {
        trackFormData(formConfig, formData);
        if (onSubmit) {
            onSubmit(formData)
        }
        return false;
    }

    const generateValidationSchema = (form) => {
        if (form) {
            const schema = form.sections.reduce((a, section) => {
                const rowData = section.fields;
                let inputsData = [...rowData];
                if (!Array.isArray(rowData)) {
                    inputsData = [rowData];
                }

                const schemaRow = inputsData.reduce((ia, row) => {
                    const inputsValidation = row
                        .filter((i) => i.validation)
                        .reduce((fa, input) => {
                            if (input.type == ADDRESS_SELECT_INPUT_TYPE) {
                                fa[`${input.name}_province`] = input.validation;
                                fa[`${input.name}_canton`] = input.validation;
                                fa[`${input.name}_district`] = input.validation;
                            } else {
                                fa[input.name] = input.validation;
                            }
                            return fa;
                        }, {});

                    return {
                        ...ia,
                        ...inputsValidation
                    }
                }, {});
                return {
                    ...a,
                    ...schemaRow
                };
            }, {});

            return Yup.object(schema);
        }
        return null;
    }

    const formik = useFormik({
        initialValues: formData,
        enableReinitialize: true,
        validationSchema: generateValidationSchema(formStructure),
        onSubmit: (values) => {
            submitForm();
        },
    });

    // useEffect(() => {
    //     scrollToFirstError(formik.errors)
    // }, [formik.errors]);

    // const scrollToFirstError = (errors) => {
    //     const firstFieldWithError = Object.keys(errors)[0];
    //     const element = document.querySelector(`[name="${firstFieldWithError}"]`);
    //     if (element) {
    //         element.scrollIntoView({ behavior: "smooth", block: "center" });
    //         element.focus();
    //     }
    // };

    const setFormValue = (fieldName, value, dataType, event) => {
        let newFormData = {
            ...formData
        };

        if (DATATYPES.ADDRESS === dataType || "phone" === dataType) {
            newFormData = {
                ...newFormData,
                ...value
            };
        } else {
            newFormData[fieldName] = value;
        }

        const reRenderFields = reRenderOptionFields.filter(f => f.reFetchAfter.includes(fieldName))
        if (reRenderFields.length > 0) {
            newFormData = reFetchSelectOption(reRenderFields, newFormData);
        }

        if (useExternalState) {
            onStateChange(newFormData);
        } else {
            setFormData(newFormData);
        }
        formik.setValues(newFormData);
        if (onInputChange) {
            onInputChange(fieldName, value, event);
        }
    }

    const reFetchSelectOption = (reRenderFields, newFormData) => {
        let newFormStructure = formStructure;
        reRenderFields.forEach(f => {
            const newOptions = f.optionsFunction(newFormData)
            newFormData[f.name] = ""
            newFormStructure = updateFieldOptions(newFormStructure, f, newOptions)
        });

        setFormStructure(newFormStructure);
        return newFormData;
    };

    const updateFieldOptions = (formStructure, field, newOptions) => {
        formStructure.sections.forEach(s => {
            s.fields.forEach(fg => {
                fg.forEach(f => {
                    if (f.name == field.name) {
                        f.options = newOptions
                    }
                })
            })
        })

        return formStructure;
    }

    const renderInput = (inputData) => {
        let { id = `i_${nanoid(10)}`, label, type, description } = inputData;
        let inputTag = generateInputTag(id, inputData, formData, (name, value, dataType, event) => setFormValue(name, value, dataType, event), formik, isEditMode);
        const isCheckboxInput = type === "checkbox";

        const InputWrapper = type !== HIDDEN_INPUT_TYPE ? FormGroup : "div"

        return (
            <InputWrapper check={isCheckboxInput} >
                {type !== HIDDEN_INPUT_TYPE && (<InputLabel
                    id={id}
                    isCheckboxInput={isCheckboxInput}
                    description={description}>
                    {label}
                </InputLabel>
                )}
                {inputTag}
            </InputWrapper>)
    }

    const renderForm = (form) => {
        return form.sections.map((section) => {
            const { hideBorderBottom = false } = section;

            const rowData = section.fields;
            let inputsData = [...rowData];
            if (!Array.isArray(rowData)) {
                inputsData = [rowData];
            }

            const sectionInputs = inputsData.map((row) => {
                const inputs = row.map((input) => {
                    const { sizing: inputSizing } = input;
                    const sizing = getInputSizing(inputSizing);
                    const hideInput = isInputHidden(input);
                    return !hideInput && (
                        input.type === HIDDEN_INPUT_TYPE ?
                            renderInput(input) :
                            <Col {...sizing} >
                                {renderInput(input)}
                            </Col>)
                });
                return (<Row>
                    {inputs}
                </Row>);
            });

            return (<Container>
                {section.title && (<h3 className={section.titleClassName || ""}>{section.title}</h3>)}
                {sectionInputs}
                {!hideBorderBottom && (<hr />)}
            </Container>);


            return
        });

    }

    const isInputHidden = (input) => {
        let hideFieldByFields = false;
        const { hideOnCreate, hideOnEdit, name } = input;
        if (visibleIdFields.length > 0) {
            const fieldInfo = visibleIdFields.find(v => v.name == input.name)
            if (fieldInfo) {
                const mustBeVisible = Object.keys(fieldInfo.visibleIf).map(f => {
                    const validationValue = fieldInfo.visibleIf[f];
                    const currentValue = formData[f];

                    if (validationValue instanceof Array) {
                        return validationValue.includes(currentValue)
                    } else {
                        return validationValue == currentValue
                    }
                }).every(Boolean)
                hideFieldByFields = !mustBeVisible;
            }
        }

        return (!isEditMode && hideOnCreate) || (isEditMode && hideOnEdit) || hideFieldByFields || false;

    }

    const getFormData = async () => {
        const error = await formik.validateForm();
        if (Object.keys(error).length > 0) {
            formik.handleSubmit();
            return false;
        } else {
            return formData;
        }
    }

    useImperativeHandle(ref, () => ({
        getFormData
    }));

    return (
        <Form ref={ref} id={props.id} onSubmit={formik.handleSubmit} className={className} inline={inline} tag={tag}>
            {formStructure && renderForm(formStructure)}
            {children}
            <LoadingScreen isLoading={isLoading} />
        </Form>
    );
});

export default DynamicForm;